import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { getInvoiceStatusPillStyle } from "./invoiceStatusStyle";

export const INVOICED_SERIES_NAME = "Invoiced";
export const COLLECTED_SERIES_NAME = "Collected";

export const INVOICE_BREAKDOWN_COLUMNS = [
  { key: "invoice_number", label: "Invoice No." },
  { key: "invoice_type", label: "Invoice Type" },
  { key: "status", label: "Status", type: "pill" },
  { key: "date", label: "Date", type: "date" },
  { key: "currency", label: "Currency" },
  { key: "raised", label: "Raised", mono: true },
  { key: "collected", label: "Collected", mono: true },
];

function toFiniteNumber(value) {
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

function formatInvoiceAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function buildInvoiceBreakdownRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: item?.invoice_number || index,
    invoice_number: item?.invoice_number || "-",
    invoice_type: item?.invoice_type || "-",
    status: { label: item?.status || "-", style: getInvoiceStatusPillStyle(item?.status) },
    date: item?.date,
    currency: item?.currency || "-",
    raised: formatInvoiceAmount(item?.raised, item?.currency),
    collected: formatInvoiceAmount(item?.collected, item?.currency),
    _raisedAmount: toFiniteNumber(item?.raised),
    _collectedAmount: toFiniteNumber(item?.collected),
  }));
}

function formatPeriodLabel(period) {
  if (!period || typeof period !== "string") return "";
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
}

// Drives the "Invoice Breakdown" modal for the invoiced_vs_collected chart:
export function useInvoiceBreakdown({ invoiceBreakdown, chartItems, onTrack }) {
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedSeriesMode, setSelectedSeriesMode] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedPeriod(null);
    setSelectedSeriesMode(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildInvoiceBreakdownRows(invoiceBreakdown), [invoiceBreakdown]);
  const canRender = allRows.length > 0;
  const canFilterByPeriod = canRender && chartItems.length > 0;

  const rows = useMemo(() => {
    let out = allRows;
    if (selectedPeriod) {
      out = out.filter((row) => typeof row.date === "string" && row.date.startsWith(selectedPeriod));
    }
    if (selectedSeriesMode === "collected") {
      out = out.filter((row) => row._collectedAmount > 0);
    } else if (selectedSeriesMode === "invoiced") {
      out = out.filter((row) => row._collectedAmount === 0);
    }
    return out;
  }, [allRows, selectedPeriod, selectedSeriesMode]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series" || !canFilterByPeriod) return;
      const period = chartItems[params.dataIndex]?.period ?? null;
      const clickedSeriesMode =
        params.seriesName === INVOICED_SERIES_NAME
          ? "invoiced"
          : params.seriesName === COLLECTED_SERIES_NAME
          ? "collected"
          : null;
      setSelectedPeriod(period);
      setSelectedSeriesMode(clickedSeriesMode);
      setModalOpen(true);
      onTrack?.(`Graph Bar Clicked: ${period || ""} ${params.seriesName || ""}`);
    },
    [chartItems, canFilterByPeriod, onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(() => {
    const parts = [];
    if (selectedPeriod) parts.push(formatPeriodLabel(selectedPeriod));
    if (selectedSeriesMode === "collected") parts.push("Collected only");
    if (selectedSeriesMode === "invoiced") parts.push("Invoiced only");
    return parts.join(" | ");
  }, [selectedPeriod, selectedSeriesMode]);

  return { rows, canRender, canFilterByPeriod, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function InvoiceBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Invoice Breakdown";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Invoice" : "Invoices"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={INVOICE_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
